import React, { useEffect, useRef, useState } from "react";
import { Grid, Typography } from "@mui/material";
import { FormattedMessage, useIntl } from "react-intl";
import { useNavigate } from "react-router-dom";

import style from "./index.module.css";

import { InputField } from "@/components/InputField";
import Button from "@/components/Button";
import SelectCountry from "../SelectCountry";

import useHelpMeChooseDoctor from "@/utils/hooks/useHelpMeChooseDoctor";
import { storage } from "@/utils/Storage";
import { defaultCountryCodeAndMobileLength } from "@/utils/Config";
import { clearLocalStorageExcept } from "@/utils";

import { useAppDispatch } from "@/store/hooks";
import { setRedirectAfterLogin } from "@/store/slices/config";
import { setAddPatient } from "@/store/slices/doctor/topNavigation";
import { setModuleName } from "@/store/slices/doctor/checkMobilePatientExistOrNot";
import { setSteps } from "@/store/slices/onboarding/onboarding";

import { IRoleType } from "@/models/role";
import { IResetAppState, ISelectCountryType, ISessionKeys } from "@/models";
import { IAddPatient, IPatientExistOrNotModule } from "@/models/doctor";
import { IOnboardingSteps } from "@/models/onboarding";

const ContactForm: React.FC = () => {
  const [formData, setFormData] = useState({
    name: "",
    phoneNumber: "",
    note: "",
  });

  const [phone, setPhone] = useState({
    number: "",
    countryCode: "",
    mobileLength: "",
  });

  const isUserLoggedIn = useRef(false);

  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  const sessionKeys = [
    ISessionKeys.ROLE_TYPE,
    ISessionKeys.NAME,
    ISessionKeys.PHONE_NUMBER,
    ISessionKeys.COUNTRY_CODE,
    ISessionKeys.USER_ID,
  ];
  const [roleType, name, phoneNumber, countryCode, userId] = sessionKeys.map(
    (key) => storage.getSessionToken(key)
  );

  const intl = useIntl();
  const { createSupportHandler } = useHelpMeChooseDoctor();

  const isFormValid =
    formData?.name?.length > 0 &&
    formData?.phoneNumber?.length > 0 &&
    formData?.note?.length > 0;

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = event?.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleRequestCallBack = async () => {
    if (!roleType) {
      navigate("/home");
      return;
    }
    if (roleType === IRoleType.GUEST) {
      dispatch({ type: IResetAppState.RESET_ALL });
      clearLocalStorageExcept(ISessionKeys.LOCATION);
      dispatch(setSteps(IOnboardingSteps.VERIFY_MOBILE_NUMBER));
      dispatch(
        setRedirectAfterLogin({
          path: "/find-doctor",
          isRedirect: true,
        })
      );
      navigate("/home");
      dispatch(setModuleName(IPatientExistOrNotModule.LOGIN));
      dispatch(
        setAddPatient({
          field: IAddPatient.MOBILE_CHECK,
          value: true,
        })
      );
      return;
    }

    await createSupportHandler({
      ...formData,
      countryCode: phone?.countryCode,
    });
    setFormData({
      ...formData,
      note: "",
    });
  };

  useEffect(() => {
    setFormData((prev) => ({
      ...prev,
      name,
      phoneNumber,
    }));
    setPhone({
      countryCode,
      number: phoneNumber,
      mobileLength: phoneNumber?.length.toString(),
    });
    if (roleType === IRoleType.PATIENT && userId) {
      isUserLoggedIn.current = true;
    }
  }, []);
  return (
    <Grid
      item
      sx={{
        display: { xs: "none", sm: "flex", md: "flex", lg: "flex", xl: "flex" },
        justifyContent: "space-between",
        alignItems: "flex-start",
        borderRadius: "1.8rem",
        background: "var(--blue-gray-100)",
        padding: "1.6rem 2rem",
      }}
    >
      <Grid item className={style.textContainer}>
        <Typography component={"h4"}>
          <FormattedMessage id="confused" defaultMessage="Confused?" /> <br />{" "}
          <FormattedMessage
            id="fill_the_form"
            defaultMessage="Fill the form our team will contact you"
          />
        </Typography>
        <Typography component={"p"}>
          <FormattedMessage
            id="team_will_assist"
            defaultMessage="Just tell us your problem, our team will assist"
          />
        </Typography>
      </Grid>
      <Grid item className={style.inputContainer}>
        <Grid item>
          <InputField
            label={intl.formatMessage({ id: "name", defaultMessage: "Name" })}
            variant="outlined"
            placeholder={
              intl.formatMessage({
                id: "name",
                defaultMessage: "Name*",
              }) + "*"
            }
            name="name"
            value={formData.name}
            onChange={handleInputChange}
            disabled={isUserLoggedIn.current}
            fullWidth
          />
        </Grid>

        <Grid item display="flex" alignItems="center">
          <SelectCountry
            disabled={isUserLoggedIn.current}
            isSmallScreen={false}
            countryCodeAndMobileLength={`${
              phone?.countryCode && phone?.mobileLength
                ? `${phone?.countryCode}_${phone?.mobileLength}`
                : defaultCountryCodeAndMobileLength
            }`}
            setCountryCodeAndMobileLength={(
              countryCode: string,
              mobileLength: string
            ) => setPhone({ ...phone, countryCode, mobileLength })}
            type={ISelectCountryType.MAKE_APPOINTMENT_MOBILE_OR_TABLET}
          />{" "}
          <InputField
            type="text"
            inputMode="numeric"
            inputProps={{ maxLength: 10, pattern: "[0-9]*" }}
            label={intl.formatMessage({
              id: "mobileNumberText",
              defaultMessage: "Mobile Number*",
            })}
            variant="outlined"
            placeholder={intl.formatMessage({
              id: "mobileNumberText",
              defaultMessage: "Mobile Number*",
            })}
            name="phoneNumber"
            value={formData.phoneNumber}
            onChange={handleInputChange}
            disabled={isUserLoggedIn.current}
            fullWidth
          />
        </Grid>

        <Grid item>
          <InputField
            label={intl.formatMessage({
              id: "problem",
              defaultMessage: "Problem",
            })}
            variant="outlined"
            placeholder={intl.formatMessage({
              id: "problem",
              defaultMessage: "Problem",
            })}
            name="note"
            value={formData.note}
            onChange={handleInputChange}
            fullWidth
          />
        </Grid>
        <Grid item className={style.submitBtn}>
          <Button disabled={!isFormValid} btnTrigger={handleRequestCallBack}>
            <FormattedMessage id="submit" defaultMessage="Submit" />
          </Button>
        </Grid>
      </Grid>
    </Grid>
  );
};

export default ContactForm;
